import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Bot, Play, ChevronDown, CheckCircle2, AlertTriangle, XCircle, Mail, Sliders, Zap } from 'lucide-react';
import GeneratorCard from '@/components/GeneratorCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };
const OUTCOME = {
  auto_issued: { label: 'Auto-Issued', color: C.green, icon: CheckCircle2 },
  issued_after_review: { label: 'Issued (reviewed)', color: C.green, icon: CheckCircle2 },
  escalated: { label: 'Escalated', color: C.amber, icon: AlertTriangle },
  declined: { label: 'Declined', color: C.red, icon: XCircle },
  declined_after_review: { label: 'Declined (reviewed)', color: C.red, icon: XCircle },
};
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Number(v || 0).toLocaleString()}`;

export default function AutonomousUWPage() {
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [mode, setMode] = useState('fields');
  const [form, setForm] = useState({ business_name: 'Harbour Bistro Pty Ltd', abn: '', sum_insured: 600000, annual_turnover: 950000, employee_count: 8, claims_count: 0, industry: 'Hospitality' });
  const [emailBody, setEmailBody] = useState('');

  const load = useCallback(() => {
    Promise.all([api.get('/pipeline/stats'), api.get('/pipeline/runs?limit=25'), api.get('/pipeline/escalations')])
      .then(([s, r, e]) => { setStats(s.data); setRuns(r.data); setEscalations(e.data); })
      .catch(err => console.error(err?.message)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const runPipeline = () => {
    setRunning(true);
    const payload = mode === 'email'
      ? { email: { body: emailBody }, class: 'sme_commercial' }
      : { fields: { ...form, sum_insured: Number(form.sum_insured), annual_turnover: Number(form.annual_turnover), employee_count: Number(form.employee_count), claims_count: Number(form.claims_count) }, class: 'sme_commercial' };
    api.post('/pipeline/run', payload)
      .then(r => { toast.success(`Pipeline complete → ${OUTCOME[r.data.outcome]?.label || r.data.outcome}`); setExpanded(r.data.run_id); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Pipeline failed'))
      .finally(() => setRunning(false));
  };

  const resolve = (runId, action) => {
    api.post(`/pipeline/runs/${runId}/resolve`, { action })
      .then(() => { toast.success(action === 'issue' ? 'Issued by underwriter' : 'Declined by underwriter'); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed'));
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  return (
    <div className="space-y-5" data-testid="autonomous-uw-page">
      {/* Hero stats */}
      <Card className="p-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #15375E 100%)', border: 'none' }} data-testid="pipeline-stats">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-4 h-4" style={{ color: C.saffron }} />
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: C.saffron }}>Autonomous Underwriting · email → score → rate → issue</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Stat label="STP Rate" value={`${stats.stp_rate}%`} accent={C.teal} />
          <Stat label="Runs" value={stats.total_runs} />
          <Stat label="Auto-Issued" value={stats.auto_issued} accent={C.green} />
          <Stat label="Escalated" value={stats.escalated} accent={C.amber} />
          <Stat label="Avg Time" value={`${stats.avg_processing_ms}ms`} />
          <Stat label="Auto GWP" value={fmt$(stats.auto_gwp)} accent={C.teal} />
        </div>
        {stats.queued > 0 && <p className="text-xs mt-3" style={{ color: C.saffron }}>{stats.queued} intake(s) queued — autonomous worker processing…</p>}
      </Card>

      <GeneratorCard onDone={load} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Run a submission */}
        <Card className="lg:col-span-1 p-4 rounded-md h-fit" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="pipeline-runner">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Run a Submission</h3>
          <div className="flex gap-1.5 mb-3">
            {[['fields', 'Structured', Sliders], ['email', 'Raw Email', Mail]].map(([m, lbl, Icon]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`pipeline-mode-${m}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-semibold"
                style={mode === m ? { background: C.navy, color: 'white' } : { background: '#EEF1F5', color: C.slate }}>
                <Icon className="w-3 h-3" />{lbl}
              </button>
            ))}
          </div>
          {mode === 'fields' ? (
            <div className="space-y-2">
              {[['business_name', 'Business name', 'text'], ['abn', 'ABN (optional — matched or simulated)', 'text'], ['industry', 'Industry', 'text'], ['sum_insured', 'Sum insured', 'number'], ['annual_turnover', 'Turnover', 'number'], ['employee_count', 'Employees', 'number'], ['claims_count', 'Claims (3yr)', 'number']].map(([k, lbl, t]) => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-xs" style={{ color: C.slate }}>{lbl}</span>
                  <input type={t} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`pipeline-field-${k}`}
                    className="w-36 px-2 py-1 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
                </div>
              ))}
              <p className="text-[10px]" style={{ color: C.slate }}>Tip: higher sums insured, prior claims or elevated risk are escalated to a human underwriter; clean low-risk SME auto-issues.</p>
            </div>
          ) : (
            <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={8}
              data-testid="pipeline-email-body"
              placeholder="Paste a broker submission email here — Gemini will extract the fields…"
              className="w-full px-2.5 py-2 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
          )}
          <button onClick={runPipeline} disabled={running} data-testid="pipeline-run-btn"
            className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-xs font-semibold"
            style={{ background: running ? '#D5DBE2' : C.green, color: 'white' }}>
            <Play className="w-3.5 h-3.5" />{running ? 'Running pipeline…' : 'Run Pipeline'}
          </button>
        </Card>

        {/* Live runs + escalations */}
        <div className="lg:col-span-2 space-y-4">
          {escalations.length > 0 && (
            <Card className="rounded-md overflow-hidden" style={{ border: `1px solid ${C.amber}40` }} data-testid="pipeline-escalations">
              <div className="p-3 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)', background: '#FFF9F0' }}>
                <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.amber }} />
                <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Human-in-the-Loop Queue ({escalations.length})</h3>
              </div>
              {escalations.map(e => (
                <div key={e.run_id} className="flex items-center justify-between gap-2 px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium" style={{ color: C.navy }}>{e.business_name}</span>
                    <p className="text-[11px]" style={{ color: C.slate }}>AIRA {e.composite_score}/10000 · {e.decision} · {fmt$(e.premium)}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => resolve(e.run_id, 'issue')} data-testid={`pipeline-issue-${e.run_id}`} className="text-[10px] font-semibold px-2.5 py-1.5 rounded" style={{ background: C.green, color: 'white' }}>Issue</button>
                    <button onClick={() => resolve(e.run_id, 'decline')} data-testid={`pipeline-decline-${e.run_id}`} className="text-[10px] font-semibold px-2.5 py-1.5 rounded" style={{ background: 'white', color: C.red, border: `1px solid ${C.red}40` }}>Decline</button>
                  </div>
                </div>
              ))}
            </Card>
          )}

          <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="pipeline-runs">
            <div className="p-3 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
              <Zap className="w-3.5 h-3.5" style={{ color: C.blue }} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Pipeline Runs</h3>
            </div>
            {runs.length === 0 && <p className="p-4 text-xs" style={{ color: C.slate }}>No runs yet — run a submission to see the autonomous trace.</p>}
            {runs.map(r => {
              const o = OUTCOME[r.outcome] || { label: r.outcome, color: C.slate, icon: Bot };
              const OIcon = o.icon; const isOpen = expanded === r.run_id;
              return (
                <div key={r.run_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <button onClick={() => setExpanded(isOpen ? null : r.run_id)} className="w-full text-left px-4 py-3 hover:bg-[#FAFBFC] flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium" style={{ color: C.navy }}>{r.business_name}</span>
                      <p className="text-[11px]" style={{ color: C.slate }}>AIRA {r.composite_score} · {fmt$(r.premium)} · {r.total_ms}ms</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded" style={{ background: o.color + '18', color: o.color }}><OIcon className="w-3 h-3" />{o.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: C.slate }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid={`pipeline-trace-${r.run_id}`}>
                      {r.stages.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px]" style={{ background: '#F4F7FA' }}>
                          <span className="font-mono font-semibold" style={{ color: C.blue }}>{i + 1}. {s.name}</span>
                          <span style={{ color: C.slate }}>{s.summary}</span>
                          <span className="font-mono" style={{ color: C.slate }}>· {s.ms}ms</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
      <p className="text-2xl font-bold font-mono mt-0.5" style={{ color: accent || 'white' }}>{value}</p>
    </div>
  );
}
